/**
 * Wikilink utilities for parsing and resolving [[slug]] and [[slug|text]] syntax
 * in markdown content.
 * 
 * Syntax:
 *   [[slug]]           → link with article title as display text (falls back to slug)
 *   [[slug|custom text]] → link with custom display text
 * 
 * Resolved HTML:
 *   <a href="/articulo/slug" class="wikilink">Display Text</a>
 *   <a href="/articulo/slug" class="wikilink broken-link" title="Artículo no encontrado">slug</a>
 */

/** Matches [[slug]] and [[slug|custom text]] — allows 1+ char slugs */
export const WIKILINK_REGEX = /\[\[([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)(?:\|([^\]]+))?\]\]/g;

/** Matches a single wikilink (non-global, for validation) */
export const WIKILINK_SINGLE_REGEX = /^\[\[([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)(?:\|([^\]]+))?\]\]$/;

/** Article route prefix */
export const ARTICLE_ROUTE_PREFIX = '/articulo';

/**
 * Resolved article info from slug lookup
 */
export interface ResolvedArticle {
  slug: string;
  title: string;
  exists: boolean;
}

/**
 * Extract all wikilink slugs from markdown content
 */
export function extractWikilinkSlugs(markdown: string): string[] {
  const slugs: string[] = [];
  let match;
  const regex = new RegExp(WIKILINK_REGEX.source, 'g');
  while ((match = regex.exec(markdown)) !== null) {
    slugs.push(match[1]);
  }
  return [...new Set(slugs)];
}

/**
 * Resolve wikilinks in markdown to standard markdown links.
 * Transforms [[slug]] → [title](/articulo/slug) before passing to markdown parser.
 * 
 * @param markdown  Raw markdown with wikilinks
 * @param articleMap  Map of slug → { title, exists }
 * @returns Markdown with wikilinks replaced by standard links
 */
export function resolveWikilinks(
  markdown: string,
  articleMap: Map<string, ResolvedArticle>
): string {
  const regex = new RegExp(WIKILINK_REGEX.source, 'g');
  
  return markdown.replace(regex, (_match, slug: string, customText?: string) => {
    const article = articleMap.get(slug);
    const displayText = customText || article?.title || formatSlugAsTitle(slug);
    const href = `${ARTICLE_ROUTE_PREFIX}/${slug}`;
    
    if (article?.exists) {
      // Existing article → standard link with wikilink class
      return `<a href="${href}" class="wikilink" data-slug="${slug}">${displayText}</a>`;
    }
    // Non-existing article → broken link with create hint
    return `<a href="${href}" class="wikilink broken-link" data-slug="${slug}" title="Artículo no encontrado">${displayText}</a>`;
  });
}

/**
 * Resolve wikilinks for display in raw text mode (edit textarea).
 * Returns the same markdown but with visual hints about link validity.
 * Used only for syntax highlighting, NOT for output.
 */
export function resolveWikilinksPreview(
  markdown: string,
  existingSlugs: Set<string>
): { text: string; links: Array<{ slug: string; exists: boolean; start: number; end: number }> } {
  const links: Array<{ slug: string; exists: boolean; start: number; end: number }> = [];
  const regex = new RegExp(WIKILINK_REGEX.source, 'g');
  let match;
  
  while ((match = regex.exec(markdown)) !== null) {
    links.push({
      slug: match[1],
      exists: existingSlugs.has(match[1]),
      start: match.index,
      end: match.index + match[0].length
    });
  }
  
  return { text: markdown, links };
}

/**
 * Convert a slug to a human-readable title
 * e.g., "attention-is-all-you-need" → "Attention Is All You Need"
 */
export function formatSlugAsTitle(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Convert a title to a URL-safe slug
 * e.g., "Attention Is All You Need" → "attention-is-all-you-need"
 */
export function titleToSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '')    // Remove special chars
    .replace(/\s+/g, '-')            // Spaces to hyphens
    .replace(/-+/g, '-')             // Collapse multiple hyphens
    .replace(/^-|-$/g, '');          // Trim hyphens
}

/**
 * Build a wikilink string from slug and optional custom text
 */
export function buildWikilink(slug: string, customText?: string): string {
  if (customText) {
    return `[[${slug}|${customText}]]`;
  }
  return `[[${slug}]]`;
}
